import { defineStore } from 'pinia';
import axios from 'axios';
import { createPayload } from '@/utils/createPayload';
import { useMainStore } from '@/stores/useMainStore';
import { useAppStore } from '@/stores/useAppStore';
import { reportSilentError, reportNonFatalError, reportFatalError } from '@/utils/storeErrorReporting';
import { getRequestUpdateError, getRequestUnavailableError, getRequestForbiddenError } from '@/utils/errorMessages';
import { useEmployeeStore } from '@/stores/useEmployeeStore';
import { useAttachmentStore } from '@/stores/useAttachmentStore';
import { useOrganizationStore } from '@/stores/useOrganizationStore';
import { displayName as getDisplayName } from '@/utils/data/display';
import { filterVisibleVolunteerRequests } from '@/utils/requests';
import { formatDate } from '@/utils/formatDate';
import formatTime from '@/utils/formatTime';
import { typeOptions, stateOptions } from '@/data/formData';
import { educatorRoles, roles, awaitingConfirmation, scheduled, canceled, pendingMatches, actionRequired } from '@/constants';

const API_DOMAIN = import.meta.env.VITE_API_URL;

/**
 * Normalizes various param formats into URLSearchParams and extracts common
 * fields
 * @param {URLSearchParams|Object|string} params - Request parameters
 * @returns {Object} Object with urlParams, paramsSlug, and extracted fields
 */
function normalizeParams(params) {
    if (!params) {
        return {
            urlParams: null,
            paramsSlug: '',
            sort: null,
            order: null,
            offset: null,
            limit: null,
        };
    }

    let urlParams;

    // Convert to URLSearchParams if needed
    if (params instanceof URLSearchParams) {
        urlParams = params;
    } else if (typeof params === 'object' && params !== null) {
        urlParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value != null) {
                // Skip null and undefined
                urlParams.append(key, value);
            }
        });
    } else {
        // String params
        urlParams = new URLSearchParams(params);
    }

    return {
        urlParams,
        paramsSlug: urlParams.toString() ? `?${urlParams.toString()}` : '',
        sort: urlParams.get('sort'),
        order: urlParams.get('order'),
        offset: urlParams.get('offset'),
        limit: urlParams.get('limit'),
    };
}

/**
 * Parses request API responses and adds computed fields
 * @param {Object} data - Raw request data from API
 * @returns {Object} Parsed request with additional computed fields
 */
function requestParse(data) {
    const { educatorFirstName, educatorLastName, educatorPreferredName, type } = data;

    const educatorName = getDisplayName({
        firstName: educatorFirstName,
        lastName: educatorLastName,
        preferredName: educatorPreferredName,
    });

    const classNameRegex = /^(.+)(?:\|\|\|)(.+)$/;
    const classNameMatch = data.className ? data.className.match(classNameRegex) : null;
    const schoolName = classNameMatch ? classNameMatch[1] : '';
    let location = classNameMatch ? classNameMatch[2] : '';
    location = location.replace(/^null, /, '');

    const date = formatDate(data.startDateTime);
    const time = formatTime(data.startDateTime * 1000, null, null, true);

    let employeeName;
    if (data.employeeId) {
        employeeName = getDisplayName({
            firstName: data.employeeFirstName,
            lastName: data.employeeLastName,
            preferredName: data.employeePreferredName,
        });
    }

    const typeDisplay = typeOptions().find((option) => option.value === type)?.text;

    return {
        ...data,
        schoolName,
        location,
        date,
        time,
        employeeName,
        educatorName,
        typeDisplay,
    };
}

export const useRequestStore = defineStore('request', {
    state: () => ({}),

    getters: {
        /**
         * Returns the current single request from mainStore
         */
        request: () => useMainStore().request,

        /**
         * Returns the apply result status
         */
        applyResult: () => useMainStore().applyResult,

        /**
         * Returns the accept direct request result status
         */
        acceptDirectRequestResult: () => useMainStore().acceptDirectRequestResult,

        /**
         * Returns the application accept result status
         */
        applicationAccept: () => useMainStore().applicationAccept,
    },

    actions: {
        /**
         * Resets pagination offset to 0 and refetches requests
         * @param {Object} payload - Contains token and data with params
         */
        async resetPaginationAndRequest(payload) {
            const { token, data } = payload;
            data.params.offset = 0;
            await this.getRequests({ token, data });
        },

        /**
         * Creates a new request for an educator
         * @param {Object} payload - Contains token and request data
         */
        async createRequest(payload) {
            try {
                const mainStore = useMainStore();
                const { id: userId, site } = mainStore.user.data;
                const siteName = site?.site_name;
                const siteCity = site?.site_city;
                const siteState = site?.site_state;

                const stateDisplay = stateOptions.find((option) => option.value === siteState)?.text || siteState;
                const cityDisplay = siteCity ? `${siteCity}, ` : '';
                let endSlug = '';

                const changedData = {
                    educatorId: userId,
                    className: `${siteName}|||${cityDisplay}${stateDisplay}`,
                };

                if (payload.data.employeeId) {
                    endSlug = `?employee_id=${payload.data.employeeId}`;
                }

                const response = await axios.post(
                    `${API_DOMAIN}/educators/${userId}/requests${endSlug}`,
                    { ...payload.data, ...changedData },
                    {
                        headers: {
                            Authorization: `Bearer ${payload.token}`,
                        },
                    },
                );

                useMainStore().updatePage({ request: requestParse(response.data) });
            } catch (error) {
                reportNonFatalError(error);
            }
        },

        /**
         * Updates an existing request (handles categories and operations)
         * @param {Object} payload - Contains token, requestId, category, operation, and update data
         */
        async updateRequest(payload) {
            try {
                let roleSlug = '';
                let endSlug = '';
                const {
                    data: { requestId },
                    token,
                } = payload;
                const { category, ...rest } = payload.data;
                const { operation } = payload.data;

                if (category) {
                    endSlug = `/${category}`;
                }

                const appStore = useAppStore();
                const mainStore = useMainStore();
                const employeeStore = useEmployeeStore();

                if (appStore.mode === roles.EDUCATOR_ROLE) {
                    roleSlug = `/educators/${mainStore.user.data.id}`;
                } else if (appStore.mode === roles.EMPLOYEE_ROLE) {
                    roleSlug = `/employees/${employeeStore.user.employeeId}`;
                }

                const response = await axios.post(`${API_DOMAIN}${roleSlug}/requests/${requestId}${endSlug}`, rest, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                useMainStore().updatePage({ request: requestParse(response.data) });

                if (operation !== 'decline') {
                    useAttachmentStore().getAttachments({ token, data: { requestId } }, 'request');
                }
            } catch (error) {
                reportNonFatalError(error, getRequestUpdateError());
            }
        },

        async updateRequestApplicant(payload) {
            const { token } = payload;
            const { requestId } = payload.data;

            try {
                const appStore = useAppStore();
                const mainStore = useMainStore();

                const educatorId = mainStore.user.data.id;
                const isEducator = appStore.mode === roles.EDUCATOR_ROLE;
                if (!isEducator || !educatorId) {
                    reportSilentError('Cannot call updateApplicants as a non-educator.');
                    return;
                }

                const response = await axios.post(`${API_DOMAIN}/educators/${educatorId}/requests/${requestId}/applicants`, payload.data, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                const { records: updatedApplicantsMeta } = response.data;
                useMainStore().updatePage({ applicants: updatedApplicantsMeta });
            } catch (error) {
                reportNonFatalError(error, getRequestUpdateError);
            }
        },

        /**
         * Fetches message-related requests
         * @param {Object} payload - Contains token and data with key
         */
        async getMessageRequests(payload) {
            try {
                const response = await axios.get(`${API_DOMAIN}/requests/messages`, {
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                    },
                });
                useMainStore().updatePage({ [payload.data.key]: response.data });
            } catch (error) {
                reportSilentError(error);
            }
        },

        /**
         * Complex multi-purpose fetching: single request, batch requests, with/without filters
         * Handles role-based routing, pagination, sorting, and parsing
         * @deprecated Use `educatorGetRequests`, `volunteerGetRequests`, or `getAdminRequests` (they have helpers for org admin and groups) depending on the role.
         * @param {Object} payload - Contains token, data with requestId/category/params
         */
        async getRequests(payload) {
            // TODO: This function DESPARATELY needs a refactor and simplification. This is incredibly hard to debug.
            useMainStore().updateLoading({ key: 'requests', status: true });

            /**
             * Helper function to handle authorization failures
             * Resets loading state and reports fatal error with unauthorized message
             */
            const handleAuthorizationFailure = () => {
                useMainStore().updateLoading({ key: 'requests', status: false });
                reportFatalError('Unauthorized request access', getRequestUnavailableError());
            };

            try {
                const { params, requestId, category } = payload.data;
                const overrideKey = payload?.data?.key;
                const appStore = useAppStore();
                const mainStore = useMainStore();

                const batchFetch = !requestId;

                let key = 'requests';
                let roleSlug = '';
                let requestIdSlug = '';
                let paramsSlug = '';
                let endSlug = '';
                let offset;

                const storedCategories = ['applications'];

                if (appStore.mode === roles.EDUCATOR_ROLE) {
                    roleSlug = `/educators/${mainStore.user.data.id}`;
                } else if (appStore.mode === roles.EMPLOYEE_ROLE) {
                    const employeeStore = useEmployeeStore();
                    roleSlug = `/employees/${employeeStore.user.employeeId}`;
                }

                if (requestId) {
                    requestIdSlug = `/${requestId}`;
                    key = 'request';
                }

                if (params) {
                    ({ paramsSlug, offset } = normalizeParams(params));

                    // exclude open requests, except for admins
                    if (appStore.mode !== roles.DE_ROLE && !paramsSlug.includes('include_direct_requests')) {
                        paramsSlug += paramsSlug.includes('?') ? '&' : '?';
                        paramsSlug += 'include_direct_requests=false';
                    }
                }

                if (category) {
                    endSlug = `/${category}`;
                    if (storedCategories.includes(category)) {
                        key = category;
                    }
                }

                key = overrideKey || key;

                const response = await axios.get(`${API_DOMAIN}${roleSlug}/requests${requestIdSlug}${endSlug}${paramsSlug}`, {
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                    },
                });

                let { data } = response;

                if (batchFetch) {
                    const { records: recordsData = [] } = data;

                    // Records have been known to return NULL from BE,
                    // which meams we have to handle it like this, not in the
                    // object destructuring above.
                    const records = recordsData ?? [];
                    const noRecords = records.length === 0;
                    if (noRecords && offset > 0) {
                        await this.resetPaginationAndRequest(payload);
                        return;
                    }

                    if (!category) {
                        mainStore.updatePagination(data);
                    }

                    data = records;
                }

                const parsedData = batchFetch ? data.map((request) => requestParse(request)) : requestParse(data);

                const { applied_filters: appliedFilters } = response.data;

                // Validate employee access for single request fetches
                if (!batchFetch && appStore.mode === roles.EMPLOYEE_ROLE) {
                    const employeeStore = useEmployeeStore();
                    const currentEmployeeId = employeeStore.userId;
                    const request = parsedData;

                    // Check if employee should have access to this request
                    const { status, employeeId, hasApplication, isDirectRequest } = request;

                    // If status is awaiting-confirmation or scheduled, employee must be the one assigned
                    if (status === awaitingConfirmation || status === scheduled) {
                        if (employeeId && employeeId !== currentEmployeeId) {
                            handleAuthorizationFailure();
                            return;
                        }
                    }

                    // If request is not open and employee has no connection to it, they shouldn't view it
                    const requestOpen = status === pendingMatches || status === actionRequired;
                    if (!requestOpen && !hasApplication && !isDirectRequest) {
                        handleAuthorizationFailure();
                        return;
                    }
                }

                useMainStore().updatePage({ [key]: parsedData });
                if (appliedFilters) {
                    useMainStore().updatePage({ appliedFilters });
                }

                useMainStore().updateLoading({ key: 'requests', status: false });
                appStore.updateError(null);
            } catch (error) {
                useMainStore().updateLoading({ key: 'requests', status: false });

                const appStore = useAppStore();
                const isSingleRequestFetch = payload.data?.requestId;

                // Use different error messages based on context
                if (error.response?.status === 401 || error.response?.status === 403) {
                    if (isSingleRequestFetch && appStore.mode === roles.EMPLOYEE_ROLE) {
                        reportFatalError(error, getRequestUnavailableError());
                        return;
                    }
                    if (isSingleRequestFetch && appStore.mode === roles.EDUCATOR_ROLE) {
                        reportFatalError(error, getRequestForbiddenError());
                        return;
                    }
                }

                reportSilentError(error);
            }
        },

        /**
         * Fetches all requests relevant for the Educator's - largest use case will be Educator Request Hub
         * TODO:2026-02-27: Educators Request Hub is not linked up to this store function yet.
         *
         * @param {Object} payload - Contains a token and URL parameters
         */
        async educatorGetRequests(payload) {
            const appStore = useAppStore();
            const mainStore = useMainStore();

            const { params, key = 'requests' } = payload.data;
            mainStore.updateLoading({ key, status: true });

            if (appStore.mode !== roles.EDUCATOR_ROLE) {
                reportSilentError('You called educatorGetRequests when the active user is not an Educator. Nothing will be done.');
                mainStore.updateLoading({ key, status: false });
                return;
            }

            const { id: educatorId = '' } = mainStore.user.data;
            if (!educatorId) {
                reportSilentError('Failed to acquire educatorId');
                mainStore.updateLoading({ key, status: false });
                return;
            }

            try {
                const { paramsSlug } = normalizeParams(params);

                const response = await axios.get(`${API_DOMAIN}/educators/${educatorId}/requests${paramsSlug}`, {
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                    },
                });

                const { records = [], limit, offset, record_count: recordsCount, applied_filters: appliedFilters } = response.data;

                const processedRecords = records.map((request) => requestParse(request));

                // For alternative keys like 'upcoming', we don't store anything
                // such as applied filters, and extra state - just the key in question.
                if (key !== 'requests') {
                    mainStore.updatePage({ [key]: processedRecords });
                    mainStore.updateLoading({ key, status: false });
                    return;
                }

                // Update pagination metadata in the store so usePagination watcher can sync state
                mainStore.updatePagination({
                    record_count: recordsCount,
                    limit: limit || 12,
                    offset: offset || 0,
                    recordsLength: processedRecords.length,
                });

                mainStore.updatePage({ requests: processedRecords, requests_length: recordsCount });

                if (appliedFilters) mainStore.updatePage({ appliedFilters });
            } catch (error) {
                reportSilentError(error);

                if (key !== 'requests') {
                    mainStore.updatePage({ [key]: [] });
                } else {
                    mainStore.updatePage({ requests: [], requests_length: 0 });
                }
            }

            mainStore.updateLoading({ key, status: false });
        },

        /**
         * Fetches all requests relevant for the Volunteers Request Hub.
         * Results will be located in the main/page/requests Pinia store.
         * @param {Object} payload - Contains token, and URL parameters
         */
        async volunteerGetRequests(payload) {
            const appStore = useAppStore();
            const mainStore = useMainStore();

            const { params, key = 'requests' } = payload.data;

            mainStore.updateLoading({ key, status: true });

            if (appStore.mode !== roles.EMPLOYEE_ROLE) {
                reportSilentError('You called volunteerGetRequests when the active user is not a volunteer. Nothing will be done.');
                mainStore.updateLoading({ key, status: false });
                return;
            }

            const employeeStore = useEmployeeStore();
            const { employeeId = '' } = employeeStore.user;
            if (!employeeId) {
                reportSilentError('Failed to acquire employeeID');
                mainStore.updateLoading({ key, status: false });
                return;
            }

            try {
                // FE handles pagination, which means we must remove limit/offset from request,
                // but keep that state for future processing
                const endpointParams = new URLSearchParams(params);
                const paramLimit = endpointParams.get('limit');
                const paramOffset = endpointParams.get('offset');

                endpointParams.delete('limit');
                endpointParams.delete('offset');

                const { paramsSlug } = normalizeParams(endpointParams);
                const response = await axios.get(`${API_DOMAIN}/employees/${employeeId}/requests${paramsSlug}`, {
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                    },
                });

                const { records = [], record_count: recordsCount, applied_filters: appliedFilters } = response.data;

                // Filter before slicing so pagination counts reflect the visible set
                const filteredRecords = filterVisibleVolunteerRequests(records, employeeId);

                const paramOffsetEnd = Number(paramOffset) + Number(paramLimit);
                const paginatedData = filteredRecords.slice(paramOffset, paramOffsetEnd) ?? [];

                const processedRecords = paginatedData.map((request) => requestParse(request));

                // For alternative keys like 'upcoming', we don't store anything
                // such as applied filters, and extra state - just the key in question.
                if (key !== 'requests') {
                    mainStore.updatePage({ [key]: processedRecords });
                    mainStore.updateLoading({ key, status: false });
                    return;
                }

                // Update pagination metadata in the store so usePagination watcher can sync state
                mainStore.updatePagination({
                    record_count: recordsCount,
                    limit: paramLimit || 12,
                    offset: paramOffset || 0,
                    recordsLength: processedRecords.length,
                });

                mainStore.updatePage({ requests: processedRecords, requests_length: recordsCount });

                if (appliedFilters) mainStore.updatePage({ appliedFilters });
            } catch (error) {
                reportSilentError(error);

                if (key !== 'requests') {
                    mainStore.updatePage({ [key]: [] });
                } else {
                    mainStore.updatePage({ requests: [], requests_length: 0 });
                }
            }

            mainStore.updateLoading({ key, status: false });
        },

        /**
         * Generalized admin request fetcher. Hits `/requests` with the provided URL parameters
         * and writes the results into the main/page/requests Pinia store. Auth payload is
         * generated internally — callers only supply URL parameters.
         * @param {URLSearchParams} [urlParameters] - URL parameters for the request
         */
        async getAdminRequests(urlParameters = new URLSearchParams()) {
            const mainStore = useMainStore();

            mainStore.updateLoading({ key: 'requests', status: true });
            try {
                const { token } = await createPayload();

                const response = await axios.get(`${API_DOMAIN}/requests?${urlParameters.toString()}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                const { records = [], record_count: recordsCount, applied_filters: appliedFilters, limit, offset } = response.data;

                mainStore.updatePagination({
                    record_count: recordsCount,
                    limit: limit ?? 12,
                    offset: offset ?? 0,
                    recordsLength: records.length,
                });

                const processedRecords = records.map((request) => requestParse(request));

                mainStore.updatePage({ requests: processedRecords, requests_length: recordsCount });

                if (appliedFilters) mainStore.updatePage({ appliedFilters });
            } catch (error) {
                reportSilentError(error);
                mainStore.updatePage({ requests: [], requests_length: 0 });
            } finally {
                mainStore.updateLoading({ key: 'requests', status: false });
            }
        },

        /**
         * Fetches all requests relevant for the Org Admins Request Hub.
         * @param {URLSearchParams} [urlParameters] - URL parameters for the request
         */
        async orgAdminGetRequests(urlParameters = new URLSearchParams()) {
            const appStore = useAppStore();
            const orgStore = useOrganizationStore();

            if (appStore.mode !== roles.ORGADMIN_ROLE) {
                reportSilentError('You called orgAdminGetRequests when the active user is not a org admin. Nothing will be done.');
                return;
            }

            const { organizationId = '' } = orgStore;
            if (!organizationId) {
                reportSilentError('Failed to acquire organization ID');
                return;
            }

            urlParameters.set('organization', organizationId);

            await this.getAdminRequests(urlParameters);
        },

        /**
         * Fetches all requests for a given organization group, scoped to the Group Admin Dashboard.
         * @param {string} groupID - The organization group UUID
         * @param {URLSearchParams} [urlParameters] - Optional additional filters
         */
        async getGroupAdminRequests(groupID, urlParameters = new URLSearchParams()) {
            if (!groupID) {
                reportSilentError('getGroupAdminRequests called without a group ID. Nothing will be done.');
                return;
            }

            urlParameters.set('organization_group_id', groupID);

            await this.getAdminRequests(urlParameters);
        },

        /**
         * WARN: Only supported by Educator and Volunteer Roles
         *
         * Gets upcoming requests that the user is scheduled to in the near future.
         * Used primarily for the UpcomingRequest component.
         * @param {Object} payload - Contains a token and optional URL params
         */
        async getUpcomingRequest(payload) {
            const appStore = useAppStore();
            const { params } = payload.data;

            const presetParams = new URLSearchParams();
            presetParams.set('limit', '1');
            presetParams.set('status', 'scheduled');
            presetParams.set('sort', 'startDate');
            presetParams.set('order', 'ASC');

            const combinedParams = new URLSearchParams(params ?? []);
            presetParams.forEach((value, key) => combinedParams.set(key, value));

            const combinedPayload = {
                ...payload,
                data: {
                    ...payload.data,
                    key: 'upcoming',
                    params: combinedParams,
                },
            };

            if (appStore.mode === roles.EDUCATOR_ROLE) {
                await this.educatorGetRequests(combinedPayload);
                return;
            }

            if (appStore.mode === roles.EMPLOYEE_ROLE) {
                await this.volunteerGetRequests(combinedPayload);
                return;
            }

            reportSilentError('User does not have permission to acquire their upcoming requests.');
        },

        /**
         * Fetches applicants for a specific request. This can determine the applicants that were direct requested,
         * and applicants that applied to an open request.
         * Data returned from this global function can be found in
         * main.page.{applicants, accepted_chat_id, and direct_requested_applicant}
         * @param {Object} payload - Contains token and requestId
         */
        async getApplicants(payload) {
            try {
                const { requestId } = payload.data;
                let roleSlug = '';

                const appStore = useAppStore();
                const mainStore = useMainStore();
                const employeeStore = useEmployeeStore();

                if (educatorRoles.includes(appStore.mode)) {
                    roleSlug = `/educators/${mainStore.user.data.id}`;
                } else if (appStore.mode === roles.EMPLOYEE_ROLE) {
                    roleSlug = `/employees/${employeeStore.user.employeeId}`;
                }

                const response = await axios.get(`${API_DOMAIN}${roleSlug}/requests/${requestId}/applicants`, {
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                    },
                });

                const { records } = response.data;
                if (!records) {
                    useMainStore().updatePage({
                        applicants: [],
                        accepted_chat_id: null,
                        direct_requested_applicant: null,
                    });
                    return;
                }

                // Direct Requests are requests where an Educator asked a specific Volunteer directly to connect.
                const directRequestedConnector = records.find((applicant) => applicant.directRequest && !applicant.declined);
                if (directRequestedConnector) {
                    useMainStore().updatePage({
                        applicants: [],
                        accepted_chat_id: directRequestedConnector.chatId,
                        direct_requested_applicant: directRequestedConnector,
                    });
                    return;
                }

                // Applicants are Volunteers that applied to an OPEN request.
                const applicants = records.filter((applicant) => !applicant.declined && !applicant.directRequest);

                // ChatId is only valid when a single applicant is present, indicating a match when they have a ChatId
                let chatId = null;
                if (applicants.length === 1 && applicants[0].chatId) {
                    ({ chatId } = applicants[0]);
                }

                useMainStore().updatePage({
                    applicants,
                    accepted_chat_id: chatId,
                    direct_requested_applicant: null,
                });
            } catch (error) {
                reportSilentError(error);
            }
        },

        /**
         * Fetches connector matches for an educator's request
         * @param {Object} payload - Contains token and requestId
         */
        async getConnectorMatches(payload) {
            try {
                const { requestId } = payload.data;
                const mainStore = useMainStore();

                const response = await axios.get(`${API_DOMAIN}/educators/${mainStore.user.data.id}/requests/${requestId}/matches`, {
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                    },
                });

                if (response.data.records) {
                    useMainStore().updatePage({ request_matches: response.data.records });
                }
            } catch (error) {
                reportSilentError(error);
            }
        },

        /**
         * Temporary implementation fetching employees with matches
         * @param {Object} payload - Contains token
         */
        async getConnectorMatchesTemp(payload) {
            try {
                const response = await axios.get(`${API_DOMAIN}/employees?limit=4&sort=matches`, {
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                    },
                });

                if (response.data.records) {
                    useMainStore().updatePage({ request_matches: response.data.records });
                }
            } catch (error) {
                reportSilentError(error);
            }
        },

        /**
         * Accepts an applicant for a request
         * @param {Object} payload - Contains token, requestId, and applicant data
         */
        async acceptApplicant(payload) {
            const { token, data, requestId } = payload;
            const category = 'applicants';
            const operation = 'accept';

            const acceptPayload = {
                token,
                data: {
                    requestId,
                    category,
                    operation,
                    ...data,
                },
            };

            const fetchPayload = { token, data: { requestId } };

            await this.updateRequest(acceptPayload);
            await this.getRequests(fetchPayload);
        },

        /**
         * Declines an applicant for a request
         * @param {Object} payload - Contains token, requestId, and applicant data
         */
        async declineApplicant(payload) {
            const { token, data } = payload;
            const { requestId } = data;
            const category = 'applicants';
            const operation = 'decline';

            const declinePayload = {
                token,
                data: {
                    requestId,
                    category,
                    operation,
                    ...data,
                },
            };

            const fetchPayload = { token, data: { requestId } };

            await this.updateRequestApplicant(declinePayload);
            await this.getRequests(fetchPayload);
        },

        /**
         * Cancels a request (role-specific: operation for employees, status for educators)
         * @param {Object} payload - Contains token and request data
         */
        async cancelRequest(payload) {
            const { token, data } = payload;
            const appStore = useAppStore();

            if (appStore.mode === roles.EMPLOYEE_ROLE) {
                const operation = 'cancel';
                const cancelPayload = {
                    token,
                    data: {
                        operation,
                        ...data,
                    },
                };
                await this.updateRequest(cancelPayload);
            } else if (appStore.mode === roles.EDUCATOR_ROLE) {
                const status = canceled;
                const statusPayload = {
                    token,
                    data: {
                        status,
                        ...data,
                    },
                };
                await this.updateRequest(statusPayload);
            }
        },

        /**
         * Fetches user's applications (pending requests the employee has applied to)
         * @param {Object} payload - Contains token
         */
        async getApplications(payload) {
            const { token } = payload;
            const mainStore = useMainStore();
            const employeeStore = useEmployeeStore();

            mainStore.updateLoading({ key: 'applications', status: true });

            const { employeeId = '' } = employeeStore.user;
            if (!employeeId) {
                mainStore.updateLoading({ key: 'applications', status: false });
                reportSilentError(new Error('Failed to acquire employeeId for getApplications'));
                return;
            }

            try {
                // Use the correct employee-specific endpoint
                const response = await axios.get(`${API_DOMAIN}/employees/${employeeId}/requests`, {
                    params: {
                        limit: 100,
                        include_direct_requests: false,
                    },
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                const { records, applied_filters: appliedFilters } = response.data;

                // ensure records is always an array
                const safeRecords = Array.isArray(records) ? records : [];

                // Parse each request using the standard parser
                const parsedApplications = safeRecords.map((request) => requestParse(request));

                mainStore.updatePage({ applications: parsedApplications });
                if (appliedFilters) {
                    mainStore.updatePage({ appliedFilters });
                }

                mainStore.updateLoading({ key: 'applications', status: false });
            } catch (error) {
                mainStore.updateLoading({ key: 'applications', status: false });
                reportSilentError(error);
                mainStore.updatePage({ applications: [] });
            }
        },

        /**
         * Submits a direct request to an employee
         * @param {Object} payload - Contains token and request data
         */
        async submitDirectRequest(payload) {
            const category = 'applicants';
            const operation = 'direct';
            const { token } = payload;

            const updatePayload = {
                token,
                data: {
                    category,
                    operation,
                    ...payload.data,
                },
            };

            await this.updateRequest(updatePayload);
        },

        /**
         * Confirms a direct request and updates results on success
         * @param {Object} payload - Contains token and request data
         */
        async confirmDirectRequest(payload) {
            const operation = 'confirm';
            const { token } = payload;

            const updatePayload = {
                token,
                data: {
                    operation,
                    ...payload.data,
                },
            };

            await this.updateRequest(updatePayload);

            const mainStore = useMainStore();
            if (mainStore.request.status === scheduled) {
                useMainStore().updateResults({
                    key: 'request_direct_accept',
                    status: 'success',
                });
            }
        },

        /**
         * Declines a direct request
         * @param {Object} payload - Contains token and request data
         */
        async declineDirectRequest(payload) {
            const operation = 'decline';
            const { token } = payload;

            const updatePayload = {
                token,
                data: {
                    operation,
                    ...payload.data,
                },
            };

            await this.updateRequest(updatePayload);
        },
    },
});
